import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useProjectBaseMapName, useProjectUpdate } from "ducks/project/hooks";
import { DEFAULT_SRID, SrId } from "@evergis/react";
import { TileInfoDc, RemoteTileServiceInfoDc } from "@evergis/api";

import { getClientData } from "../../ducks/project/selectors";
import { getLocation } from "../../ducks/location/selectors";
import { useTileService } from "../layer/useTileService";
import { useBasemapSelectModule } from "../appConfig/useAppModule";
import { useWidgetConfiguration } from "../../modules/Dashboard/hooks/useWidgetConfiguration";

import { BaseMapItem } from "../../modules/MapControls/components/BasemapSelect/BasemapBlock";
import { WidgetType } from "../../modules/FeatureCard/types";

// eslint-disable-next-line max-lines-per-function
export function useBaseMap() {
  const { options } = useBasemapSelectModule();
  const baseMapName = useProjectBaseMapName();
  const clientData = useSelector(getClientData);
  const mutation = useProjectUpdate();

  const baseMapItems: BaseMapItem[] = useMemo(
    () => clientData?.baseMapItems || (options ? (options.items as BaseMapItem[]) : []),
    [clientData?.baseMapItems, options],
  );
  const currentBaseMap = useMemo(
    () => baseMapItems.find(({ name }) => name === baseMapName),
    [baseMapItems, baseMapName],
  );

  const updateMap = useCallback(
    (mapName: string) => {
      mutation({
        content: {
          baseMapName: mapName,
        },
      });
    },
    [mutation],
  );

  const updateMapSettings = useCallback(
    (baseMap: Partial<BaseMapItem>) => {
      mutation({
        content: {
          clientData: {
            ...clientData,
            baseMapItems: baseMapItems.map(item => {
              if (item.name === baseMap.name) {
                return {
                  ...item,
                  ...baseMap,
                };
              }

              return item;
            }),
          },
        },
      });
    },
    [mutation, baseMapItems, clientData],
  );

  const addMap = useCallback(
    (baseMap: BaseMapItem) => {
      mutation({
        content: {
          baseMapName: baseMap.name,
          clientData: {
            ...clientData,
            baseMapItems: [baseMap, ...baseMapItems],
          },
        },
      });
    },
    [mutation, baseMapItems, clientData],
  );

  const deleteMap = useCallback(
    (mapName: string) => {
      mutation({
        content: {
          clientData: {
            ...clientData,
            baseMapItems: baseMapItems.filter(({ name }) => name !== mapName),
          },
        },
      });
    },
    [mutation, baseMapItems, clientData],
  );

  return {
    baseMapItems,
    baseMapName,
    currentBaseMap,
    addMap,
    updateMap,
    deleteMap,
    updateMapSettings,
  };
}

export const useBaseMapTileInfo = (): TileInfoDc | undefined | null => {
  const basemap = useProjectBaseMapName();
  const { tileLayer, loading, error } = useTileService(basemap);

  if (error) {
    return null;
  }

  if (tileLayer && !loading) {
    return tileLayer.tileInfo || null;
  }

  return undefined;
};

export const useTileServiceInfo = (): RemoteTileServiceInfoDc | undefined | null => {
  const basemap = useProjectBaseMapName();
  const { tileLayer, loading, error } = useTileService(basemap);

  if (error) {
    return null;
  }

  if (tileLayer && !loading) {
    return tileLayer || null;
  }

  return undefined;
};

export const useBaseMapProperties = () => {
  const { baseMapItems, baseMapName } = useBaseMap();

  return useMemo(
    () => baseMapItems.find(({ name }) => name === baseMapName) || ({} as BaseMapItem),
    [baseMapItems, baseMapName],
  );
};

export const useBaseMapSubDomains = (): string[] | undefined | null => {
  const tileInfo = useBaseMapTileInfo();

  return (tileInfo && tileInfo.tilesSubDomains) || null;
};

export const useBaseMapSrid = (): SrId | undefined | null => {
  const tileInfo = useBaseMapTileInfo();
  const { currentConfig } = useWidgetConfiguration(WidgetType.Dashboard);
  const location = useSelector(getLocation);
  const [srid, setSrid] = useState(DEFAULT_SRID);

  const { srid: configSrid } = currentConfig?.options || {};
  const { srid: locationSrid } = location.query || {};

  useEffect(() => {
    if (tileInfo?.sr) {
      setSrid(tileInfo.sr);
    }
  }, [tileInfo?.sr]);

  useEffect(() => {
    if (configSrid) {
      setSrid(configSrid);
    }
  }, [configSrid]);

  useEffect(() => {
    if (locationSrid) {
      setSrid(locationSrid);
    }
  }, [locationSrid]);

  return srid;
};
